import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { SEO } from '@/components/SEO';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { MapPin, Clock, Phone, Mail } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const dayKeys = ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;

const Location = () => {
  const { t } = useTranslation(['location', 'seo', 'common']);
  const swiperRef = useRef<SwiperType>();
  const hours = t('location:hours', { returnObjects: true }) as Record<string, string>;

  // Lista delle immagini della location
  const locationImages = [
    '/img/location/IMG_8110.jpg',
    '/img/location/IMG_8113.jpg',
    '/img/location/IMG_8127.jpg',
    '/img/location/IMG_8189.jpg',
    '/img/location/IMG_8261.jpg',
    '/img/location/IMG_8266.jpg',
    '/img/location/IMG_8270.jpg',
    '/img/location/IMG_8276.jpg',
    '/img/location/IMG_8281.jpg',
  ];

  const locationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: 'Brace e Sapori',
    image: 'https://braceesapori.it/img/location/IMG_8110.jpg',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Via P. B. Moscatelli, 8',
      addressLocality: 'San Giorgio Ionico',
      postalCode: '74027',
      addressRegion: 'TA',
      addressCountry: 'IT',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 40.4564,
      longitude: 17.2036,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'],
        opens: '17:00',
        closes: '00:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Sunday'],
        opens: '09:00',
        closes: '00:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday'],
        opens: '10:30',
        closes: '00:30',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Tuesday'],
        opens: '17:00',
        closes: '00:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Thursday'],
        opens: '17:00',
        closes: '00:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Friday'],
        opens: '17:00',
        closes: '00:00',
      },
    ],
  };

  return (
    <main id="main-content" className="min-h-screen bg-background">
      <SEO
        title={t('seo:location.title')}
        description={t('seo:location.description')}
        keywords={t('seo:location.keywords')}
        ogImage="/img/location/IMG_8110.jpg"
        logicalPath="/location"
        structuredData={locationStructuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[50vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/img/location/IMG_8110.jpg"
            alt={t('location:hero.imageAlt')}
            className="h-full w-full object-cover"
            loading="eager"
            decoding="async"
            width="1920"
            height="1080"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        </div>
        
        <div className="relative z-10 flex min-h-[50vh] items-center px-6 py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-2xl">
              <ScrollReveal>
                <span className="mb-4 inline-block text-sm font-light uppercase tracking-[0.3em] text-primary">
                  {t('location:hero.label')}
                </span>
              </ScrollReveal>
              
              <ScrollReveal delay={100}>
                <h1 className="mb-6 text-5xl font-bold text-foreground md:text-6xl lg:text-7xl">
                  {t('location:hero.title')}
                  <br />
                  <span className="text-primary">{t('location:hero.titleOf')}</span>
                </h1>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <p className="text-xl font-light leading-relaxed text-muted-foreground">
                  {t('location:hero.subtitle')}
                </p>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-24 px-6">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="mb-16 text-center">
              <h2 className="mb-6 text-4xl font-semibold text-foreground md:text-5xl">
                {t('location:welcome.title')}
              </h2>
              <div className="mx-auto mt-8 max-w-3xl space-y-6 text-lg font-light leading-relaxed text-muted-foreground">
                <p>{t('location:welcome.p1')}</p>
                <p>{t('location:welcome.p2')}</p>
                <p>{t('location:welcome.p3')}</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="bg-card py-24 px-6">
        <div className="mx-auto max-w-7xl">
          <ScrollReveal>
            <div className="mb-16 text-center">
              <span className="mb-4 inline-block text-sm font-light uppercase tracking-[0.3em] text-primary">
                {t('location:gallery.label')}
              </span>
              <h2 className="text-4xl font-semibold text-foreground md:text-5xl">
                {t('location:gallery.title')}
              </h2>
              <p className="mt-4 text-muted-foreground">{t('location:gallery.subtitle')}</p>
            </div>
          </ScrollReveal>

          {/* Swiper Slider */}
          <ScrollReveal delay={100}>
            <div className="relative w-full max-w-full overflow-hidden">
              <Swiper
                modules={[Navigation, Pagination, Autoplay, EffectFade]}
                spaceBetween={30}
                slidesPerView={1}
                navigation={{
                  prevEl: '.swiper-button-prev-location',
                  nextEl: '.swiper-button-next-location',
                  enabled: false,
                }}
                breakpoints={{
                  640: {
                    slidesPerView: 1,
                  },
                  768: {
                    slidesPerView: 2,
                    navigation: {
                      enabled: true,
                    },
                  },
                  1024: {
                    slidesPerView: 3,
                  },
                }}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: false,
                }}
                loop={true}
                onBeforeInit={(swiper) => {
                  swiperRef.current = swiper;
                }}
                className="!pb-12 !w-full !max-w-full"
                style={{ width: '100%', maxWidth: '100%' }}
              >
                {locationImages.map((image, index) => (
                  <SwiperSlide key={index} className="!w-full">
                    <div className="relative aspect-[4/3] overflow-hidden rounded-lg w-full">
                      <img
                        src={image}
                        alt={t('location:gallery.slideAlt', { n: index + 1 })}
                        className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                        loading={index < 1 ? 'eager' : 'lazy'}
                        decoding="async"
                        width="1200"
                        height="900"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Custom Navigation Buttons - Hidden on mobile */}
              <button
                className="swiper-button-prev-location hidden md:flex absolute left-4 top-1/2 z-10 -translate-y-1/2 h-12 w-12 items-center justify-center rounded-full bg-background/80 backdrop-blur-sm text-foreground transition-all hover:bg-background hover:text-primary"
                aria-label={t('common:aria.prevSlide')}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                className="swiper-button-next-location hidden md:flex absolute right-4 top-1/2 z-10 -translate-y-1/2 h-12 w-12 items-center justify-center rounded-full bg-background/80 backdrop-blur-sm text-foreground transition-all hover:bg-background hover:text-primary"
                aria-label={t('common:aria.nextSlide')}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-24 px-6">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="rounded-lg border border-border bg-card p-8 md:p-12">
              <h3 className="mb-8 text-center text-3xl font-semibold text-foreground">
                {t('location:directions.title')}
              </h3>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/20">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="mb-2 text-lg font-semibold text-foreground">{t('location:directions.address')}</h4>
                    <p className="text-muted-foreground">
                      {t('location:directions.addressLine1')}
                      <br />
                      {t('location:directions.addressLine2')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/20">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="mb-2 text-lg font-semibold text-foreground">{t('location:directions.hours')}</h4>
                    <p className="text-muted-foreground">
                      {dayKeys.map((day) => (
                        <span key={day} className="block">
                          {t(`common:days.${day}`)}:{' '}
                          {hours[day] === 'Chiuso' || hours[day] === 'Closed' ? t('common:closed') : hours[day]}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/20">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="mb-2 text-lg font-semibold text-foreground">{t('location:directions.phone')}</h4>
                    <a 
                      href="tel:+393472916493" 
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      347 291 6493
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/20">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="mb-2 text-lg font-semibold text-foreground">{t('location:directions.email')}</h4>
                    <a 
                      href="mailto:braceesapori@yahoo.com" 
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      braceesapori@yahoo.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Location;

